import React from "react";
import style from "../../assets/css/static-page.module.scss";
import {Grid} from "@mui/system";
import {
  Breadcrumbs,
  Button,
  FormControl,
  FormLabel,
  Typography,
} from "@mui/material";
import {Link, useNavigate} from "react-router-dom";
import GlobalTextField from "../global-fields/GlobalTextField";
import GlobalTextAriaField from "../global-fields/GlobalTextArea";
import EmailIcon from "../../assets/img/icons/email.svg";
import PhoneIcon from "../../assets/img/icons/call.svg";
import AddressIcon from "../../assets/img/icons/address.svg";
import {isMobileOnly} from "react-device-detect";
import * as yup from "yup";
import {
  ADDRESS_LINK,
  ERR_MESSAGE,
  REQUIRED_FIELD,
} from "../../constants/constants";
import {useFormik} from "formik";
import {useDispatch} from "react-redux";
import {setUiData} from "../../store/uiSlice";
import {CONTACT_US} from "../../constants/apiConstant";
import GlobalNotificationPopup from "../global-fields/GlobalNotificationPopup";
import {removeCookies} from "../../constants/helper";

const ContactUs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialFormikVals = {
    full_name: "",
    email: "",
    subject: "",
    message: "",
  };

  const formik = useFormik({
    initialValues: initialFormikVals,
    validationSchema: yup.object().shape({
      full_name: yup
        .string()
        .trim()
        .required(REQUIRED_FIELD)
        .min(2, "Full Name must be at least 2 characters."),
      email: yup
        .string()
        .email("Must be a valid email.")
        .required(REQUIRED_FIELD),
      subject: yup
        .string()
        .required(REQUIRED_FIELD)
        .min(2, "Subject must be at least 2 characters."),
      message: yup
        .string()
        .required(REQUIRED_FIELD)
        .max(250, "Maximum 250 Characters Allowed."),
    }),
    onSubmit: async (values, {resetForm}) => {
      dispatch(setUiData({showLoader: true}));

      try {
        const response = await fetch(CONTACT_US.url, {
          method: CONTACT_US.method,
          headers: CONTACT_US.headers,
          body: JSON.stringify(values),
        });

        const res = await response.json();

        dispatch(setUiData({showLoader: false}));

        if (res.status == 1) {
          GlobalNotificationPopup({type: "success", message: res?.message});
          resetForm();
        } else {
          GlobalNotificationPopup({type: "error", message: res?.message});
        }
      } catch (error) {
        console.error("Error:", error);
        removeCookies("isLogin");
        dispatch(setUiData({showLoader: false}));
        GlobalNotificationPopup({
          type: "error",
          message: ERR_MESSAGE,
        });
        navigate("/");
      }
    },
  });

  return (
    <>
      <section className={style.contactSection}>
        <Grid container spacing={2}>
          <Grid size={{xs: 12}} sx={{m: 2}}>
            <div className={style.staticPageBanner}>
              <div>
                <span>CONTACT US</span>
                <h1> Get in touch </h1>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link underline="hover" color="inherit" to="/">
                    Home
                  </Link>
                  <Typography sx={{color: "text.primary"}}>
                    Contact Us
                  </Typography>
                </Breadcrumbs>
              </div>
            </div>
          </Grid>
        </Grid>
      </section>

      <section className={style.contactFormSection}>
        <Grid
          container
          spacing={2}
          className="customeContainer"
          justifyContent="center"
        >
          <Grid size={{xs: 12, lg: 5}}>
            <div className={style.contactFormMain}>
              <form
                autoComplete="false"
                autoSave="false"
                onSubmit={formik.handleSubmit}
              >
                <Grid container spacing={2}>
                  <Grid size={{xs: 12}}>
                    <div className={style.contactTitle}>
                      <h2>
                        Have Questions?
                        <br />
                        We’re Just a Message Away!
                      </h2>
                    </div>
                  </Grid>

                  <Grid
                    size={{xs: 12, md: 12}}
                    sx={{mb: {xs: 1, md: 2}}}
                    className="test"
                  >
                    <FormControl fullWidth>
                      <FormLabel sx={{mb: 1}}> Full Name </FormLabel>
                    </FormControl>
                    <GlobalTextField
                      id="full_name"
                      name="full_name"
                      placeH="Enter your full name"
                      type="text"
                      value={formik.values.full_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.full_name &&
                        Boolean(formik.errors.full_name)
                      }
                      helperText={
                        formik.touched.full_name && formik.errors.full_name
                      }
                    />
                  </Grid>
                  <Grid size={{xs: 12, md: 12}} sx={{mb: {xs: 1, md: 2}}}>
                    <FormControl fullWidth>
                      <FormLabel sx={{mb: 1}}> Email </FormLabel>
                    </FormControl>
                    <GlobalTextField
                      id="email"
                      name="email"
                      type="email"
                      placeH="Enter your email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid size={{xs: 12, md: 12}} sx={{mb: {xs: 1, md: 2}}}>
                    <FormControl fullWidth>
                      <FormLabel sx={{mb: 1}}> Subject </FormLabel>
                    </FormControl>
                    <GlobalTextField
                      id="subject"
                      name="subject"
                      type="text"
                      placeH="Your subject of the message"
                      value={formik.values.subject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.subject && Boolean(formik.errors.subject)
                      }
                      helperText={
                        formik.touched.subject && formik.errors.subject
                      }
                    />
                  </Grid>
                  <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                    <FormControl fullWidth>
                      <FormLabel sx={{mb: 1}}> Message </FormLabel>
                    </FormControl>
                    <GlobalTextAriaField
                      row={5}
                      placeH="Write your message.."
                      id="message"
                      name="message"
                      type="message"
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.message && Boolean(formik.errors.message)
                      }
                      helperText={
                        formik.touched.message && formik.errors.message
                      }
                    />
                  </Grid>
                  <Grid size={{xs: 12}}>
                    <Button
                      style={{height: 45, letterSpacing: 1}}
                      type="submit"
                      variant="contained"
                    >
                      {" "}
                      Send Message{" "}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </div>
          </Grid>
          <Grid size={{xs: 12, lg: 6}}>
            <Grid container spacing={2}>
              <Grid size={{xs: 12}} sx={{order: {xs: 2, md: 1}}}>
                <div className={style.contactMapMain}>
                  <iframe
                    src="https://maps.google.com/maps?width=520&amp;height=400&amp;hl=en&amp;q=608%20Fifth%20Avenue%20Suite%20901%20New%20York,%20NY%2010020%20+(Surya%20AIB)&amp;t=&amp;z=12&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                    width="100%"
                    height={isMobileOnly ? 200 : 350}
                    style={{border: 0}}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Map"
                  />
                </div>
              </Grid>
              <Grid size={{xs: 12}} sx={{order: {xs: 1, md: 2}}}>
                <div
                  className={style.contactDetails}
                  style={{marginTop: "5px"}}
                  onClick={(e) => {
                    window.location.href = `mailto:office@aibnewyork.com`;
                  }}
                >
                  <img alt="email" src={EmailIcon} />
                  <div>
                    <h2> Email </h2>
                    <p> office@aibnewyork.com</p>
                    {/* <p> info@suryains.com</p> */}
                  </div>
                </div>
                <div
                  className={style.contactDetails}
                  onClick={(e) => {
                    window.location.href = `tel:+12124895300`;
                  }}
                >
                  <img alt="phone" src={PhoneIcon} />
                  <div>
                    <h2> Phone </h2>
                    <p> +1 (212)-489-5300 </p>
                  </div>
                </div>
                <div
                  className={style.contactDetails}
                  onClick={() => {
                    window.open(ADDRESS_LINK, "_blank");
                  }}
                >
                  <img alt="address" src={AddressIcon} />
                  <div>
                    <h2> Address </h2>
                    <p> 608 5th Ave, Suite 903 New York NY 10020</p>
                  </div>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </section>
    </>
  );
};

export default ContactUs;
